import React, { useEffect, useMemo, useState } from 'react'

import {
    Box,
    Button,
    Divider,
    MenuItem,
    TextField,
    useTheme,
} from '@material-ui/core'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import CancelIcon from '@material-ui/icons/Cancel'
import HomeIcon from '@material-ui/icons/Home'
import AllOutIcon from '@material-ui/icons/AllOut'
import PeopleIcon from '@material-ui/icons/People'
import SelectAllIcon from '@material-ui/icons/SelectAll'
import ClearAllIcon from '@material-ui/icons/ClearAll'

import { Container, SideDrawer } from '../../../components'
import { typographyStyle } from '../../../theme/typographyStyle'
import { getServiceImageDetails, User } from '../../../models'
import {
    ActionItemSuggestion,
    SuggestedService,
    SuggestedVendor,
} from '../../../hooks/useActionItemSuggestions/types'

interface ActionItemBulkActionDrawerProps {
    open: boolean
    onClose: () => void
    data: { suggestions: ActionItemSuggestion[] }
    selectedCount: number
    onSelectAll: () => void
    onDeselectAll: () => void
    userList: User[]
    onBulkAccept: (
        serviceId: number | null,
        vendorId: number | null,
        myTeamMode: boolean,
        userId: number | null,
    ) => void
    onBulkReject: () => void
    minConfidence: number | null
    avgConfidence: number | null
    maxConfidence: number | null
}

export const ActionItemBulkActionDrawer: React.FC<ActionItemBulkActionDrawerProps> = ({
    open,
    onClose,
    data,
    selectedCount,
    onSelectAll,
    onDeselectAll,
    userList,
    onBulkAccept,
    onBulkReject,
    minConfidence,
    avgConfidence,
    maxConfidence,
}) => {
    const theme = useTheme()

    const [bulkServiceId, setBulkServiceId] = useState<number | null>(null)
    const [bulkVendorId, setBulkVendorId] = useState<number | null>(null)
    const [bulkMyTeamMode, setBulkMyTeamMode] = useState(false)
    const [bulkUserId, setBulkUserId] = useState<number | null>(null)

    useEffect(() => {
        if (!open) {
            setBulkServiceId(null)
            setBulkVendorId(null)
            setBulkMyTeamMode(false)
            setBulkUserId(null)
        }
    }, [open])

    const allServices = useMemo(() => {
        const serviceMap = new Map<number, SuggestedService>()
        data.suggestions.forEach((s) => {
            s.suggested_services.forEach((svc) => {
                if (!serviceMap.has(svc.service_id)) {
                    serviceMap.set(svc.service_id, svc)
                }
            })
        })
        return Array.from(serviceMap.values())
    }, [data])

    const vendorsForService = useMemo(() => {
        if (bulkServiceId === null) return [] as SuggestedVendor[]
        const vendorMap = new Map<number, SuggestedVendor>()
        data.suggestions.forEach((s) => {
            const svc = s.suggested_services.find(
                (sv) => sv.service_id === bulkServiceId,
            )
            if (svc) {
                svc.suggested_vendors.forEach((v) => {
                    if (!vendorMap.has(v.vendor_id)) {
                        vendorMap.set(v.vendor_id, v)
                    }
                })
            }
        })
        return Array.from(vendorMap.values())
    }, [data, bulkServiceId])

    useEffect(() => {
        if (
            bulkVendorId !== null &&
            !vendorsForService.some((v) => v.vendor_id === bulkVendorId)
        ) {
            setBulkVendorId(null)
        }
    }, [vendorsForService, bulkVendorId])

    return (
        <SideDrawer
            open={open}
            handleClose={onClose}
            title="Bulk Actions"
            width={380}
        >
            <Container
                style={{
                    flex: 1,
                    flexDirection: 'column',
                    padding: theme.spacing(2),
                    gap: theme.spacing(2),
                }}
            >
                {/* Selection count & controls */}
                <Container
                    style={{
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}
                >
                    <span
                        style={{
                            ...typographyStyle(theme.typography.subtitle1),
                            fontWeight: 600,
                        }}
                    >
                        {selectedCount} selected
                    </span>
                    <Container style={{ gap: theme.spacing(1) }}>
                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={<SelectAllIcon />}
                            onClick={onSelectAll}
                            style={{ textTransform: 'none' }}
                        >
                            Select All
                        </Button>
                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={<ClearAllIcon />}
                            onClick={onDeselectAll}
                            style={{ textTransform: 'none' }}
                        >
                            Deselect All
                        </Button>
                    </Container>
                </Container>

                {/* Filtered Set Confidence Stats */}
                <div>
                    <span
                        style={{
                            ...typographyStyle(theme.typography.caption),
                            fontWeight: 600,
                            color: theme.palette.text.secondary,
                            textTransform: 'uppercase',
                            letterSpacing: 0.5,
                            marginBottom: theme.spacing(0.5),
                            display: 'block',
                        }}
                    >
                        Filtered Set Confidence
                    </span>
                    <Container style={{ gap: theme.spacing(1) }}>
                        {([
                            {
                                label: 'Min',
                                value: minConfidence,
                                color: theme.palette.warning.main,
                            },
                            {
                                label: 'Avg.',
                                value: avgConfidence,
                                color: theme.palette.info.main,
                            },
                            {
                                label: 'Max',
                                value: maxConfidence,
                                color: theme.palette.success.main,
                            },
                        ] as const).map((tile) => (
                            <Box
                                key={tile.label}
                                style={{
                                    padding: theme.spacing(1),
                                    border: `1px solid ${theme.palette.divider}`,
                                    borderRadius: theme.spacing(1),
                                    backgroundColor:
                                        theme.palette.background.paper,
                                    display: 'flex',
                                    alignItems: 'stretch',
                                    flex: 1,
                                }}
                            >
                                <Box
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'flex-start',
                                        gap: theme.spacing(0.5),
                                        width: '100%',
                                    }}
                                >
                                    <span
                                        style={{
                                            fontSize: '0.85rem',
                                            color: theme.palette.text.secondary,
                                            fontWeight: 500,
                                            lineHeight: 1.2,
                                        }}
                                    >
                                        {tile.label}
                                    </span>
                                    <span
                                        style={{
                                            width: '100%',
                                            textAlign: 'right',
                                            fontSize: '1.5rem',
                                            fontWeight: 700,
                                            color: tile.color,
                                            lineHeight: 1.2,
                                        }}
                                    >
                                        {tile.value !== null
                                            ? `${Math.round(tile.value * 100)}%`
                                            : 'NA'}
                                    </span>
                                </Box>
                            </Box>
                        ))}
                    </Container>
                </div>

                <Divider />

                {/* Service selector */}
                <div>
                    <span
                        style={{
                            ...typographyStyle(theme.typography.caption),
                            fontWeight: 600,
                            color: theme.palette.text.secondary,
                            textTransform: 'uppercase',
                            letterSpacing: 0.5,
                            marginBottom: theme.spacing(0.5),
                            display: 'block',
                        }}
                    >
                        Service
                    </span>
                    <TextField
                        select
                        fullWidth
                        variant="outlined"
                        size="small"
                        value={bulkServiceId ?? ''}
                        onChange={(e) =>
                            setBulkServiceId(Number(e.target.value))
                        }
                        SelectProps={{
                            displayEmpty: true,
                            renderValue: (val) => {
                                if (!val) {
                                    return (
                                        <span
                                            style={{
                                                color:
                                                    theme.palette.text.disabled,
                                            }}
                                        >
                                            Select service...
                                        </span>
                                    )
                                }
                                const svc = allServices.find(
                                    (s) => s.service_id === val,
                                )
                                if (!svc) return ''
                                const imgDetails = getServiceImageDetails(
                                    svc.service_id,
                                    svc.service_category,
                                )
                                return (
                                    <span
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 6,
                                        }}
                                    >
                                        {imgDetails.icon && (
                                            <img
                                                src={imgDetails.icon}
                                                style={{
                                                    maxHeight: 20,
                                                    maxWidth: 22,
                                                }}
                                            />
                                        )}
                                        {svc.service_name}
                                    </span>
                                )
                            },
                        }}
                    >
                        {allServices.map((service) => {
                            const imgDetails = getServiceImageDetails(
                                service.service_id,
                                service.service_category,
                            )
                            return (
                                <MenuItem
                                    key={service.service_id}
                                    value={service.service_id}
                                >
                                    {imgDetails.icon && (
                                        <img
                                            src={imgDetails.icon}
                                            style={{
                                                maxHeight: 20,
                                                maxWidth: 22,
                                                marginRight: 6,
                                            }}
                                        />
                                    )}
                                    {service.service_name}
                                </MenuItem>
                            )
                        })}
                    </TextField>
                </div>

                {/* Vendor / My Team selector */}
                <div>
                    <Container
                        style={{
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            marginBottom: theme.spacing(0.5),
                        }}
                    >
                        <span
                            style={{
                                ...typographyStyle(theme.typography.caption),
                                fontWeight: 600,
                                color: theme.palette.text.secondary,
                                textTransform: 'uppercase',
                                letterSpacing: 0.5,
                            }}
                        >
                            {bulkMyTeamMode ? 'Assignee' : 'Vendor'}
                        </span>
                        <Button
                            variant={bulkMyTeamMode ? 'contained' : 'outlined'}
                            color={bulkMyTeamMode ? 'primary' : 'default'}
                            size="small"
                            startIcon={<PeopleIcon />}
                            onClick={() => setBulkMyTeamMode((prev) => !prev)}
                            style={{ textTransform: 'none' }}
                        >
                            My Team
                        </Button>
                    </Container>

                    {bulkMyTeamMode ? (
                        <TextField
                            select
                            fullWidth
                            variant="outlined"
                            size="small"
                            value={bulkUserId ?? ''}
                            onChange={(e) =>
                                setBulkUserId(Number(e.target.value))
                            }
                            disabled={userList.length === 0}
                            SelectProps={{
                                displayEmpty: true,
                                renderValue: (val) => {
                                    if (!val) {
                                        return (
                                            <span
                                                style={{
                                                    color:
                                                        theme.palette.text
                                                            .disabled,
                                                }}
                                            >
                                                Select team member...
                                            </span>
                                        )
                                    }
                                    const u = userList.find(
                                        (user) => user.id === val,
                                    )
                                    if (!u) return ''
                                    return (
                                        <span
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 6,
                                            }}
                                        >
                                            <PeopleIcon
                                                style={{
                                                    fontSize: 18,
                                                    color:
                                                        theme.palette.text
                                                            .secondary,
                                                }}
                                            />
                                            {u.name}
                                        </span>
                                    )
                                },
                            }}
                        >
                            {userList.map((user) => (
                                <MenuItem key={user.id} value={user.id}>
                                    <PeopleIcon
                                        style={{
                                            fontSize: 18,
                                            color: theme.palette.text.secondary,
                                            marginRight: 6,
                                        }}
                                    />
                                    {user.name}
                                </MenuItem>
                            ))}
                        </TextField>
                    ) : (
                        <TextField
                            select
                            fullWidth
                            variant="outlined"
                            size="small"
                            value={
                                vendorsForService.some(
                                    (v) => v.vendor_id === bulkVendorId,
                                )
                                    ? bulkVendorId
                                    : ''
                            }
                            onChange={(e) =>
                                setBulkVendorId(Number(e.target.value))
                            }
                            disabled={vendorsForService.length === 0}
                            SelectProps={{
                                displayEmpty: true,
                                renderValue: (val) => {
                                    if (!val) {
                                        return (
                                            <span
                                                style={{
                                                    color:
                                                        theme.palette.text
                                                            .disabled,
                                                }}
                                            >
                                                {bulkServiceId === null
                                                    ? 'Select a service first...'
                                                    : 'Select vendor...'}
                                            </span>
                                        )
                                    }
                                    const v = vendorsForService.find(
                                        (vn) => vn.vendor_id === val,
                                    )
                                    if (!v) return ''
                                    const VendorIcon = v.in_house
                                        ? HomeIcon
                                        : AllOutIcon
                                    return (
                                        <span
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 6,
                                            }}
                                        >
                                            <VendorIcon
                                                style={{
                                                    fontSize: 18,
                                                    color:
                                                        theme.palette.text
                                                            .secondary,
                                                }}
                                            />
                                            {v.vendor_name}
                                        </span>
                                    )
                                },
                            }}
                        >
                            {vendorsForService.map((vendor) => {
                                const VendorIcon = vendor.in_house
                                    ? HomeIcon
                                    : AllOutIcon
                                return (
                                    <MenuItem
                                        key={vendor.vendor_id}
                                        value={vendor.vendor_id}
                                    >
                                        <VendorIcon
                                            style={{
                                                fontSize: 18,
                                                color:
                                                    theme.palette.text
                                                        .secondary,
                                                marginRight: 6,
                                            }}
                                        />
                                        {vendor.vendor_name}
                                    </MenuItem>
                                )
                            })}
                        </TextField>
                    )}
                </div>

                <Divider />

                {/* Accept / Reject buttons */}
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: theme.spacing(1),
                    }}
                >
                    <Button
                        variant="outlined"
                        fullWidth
                        startIcon={<CheckCircleIcon />}
                        onClick={() =>
                            onBulkAccept(
                                bulkServiceId,
                                bulkVendorId,
                                bulkMyTeamMode,
                                bulkUserId,
                            )
                        }
                        disabled={selectedCount === 0}
                        style={{
                            textTransform: 'none',
                            color: theme.palette.success.main,
                            borderColor: theme.palette.success.main,
                        }}
                    >
                        Accept {selectedCount} Suggestion
                        {selectedCount !== 1 ? 's' : ''}
                    </Button>
                    <Button
                        variant="outlined"
                        fullWidth
                        startIcon={<CancelIcon />}
                        onClick={onBulkReject}
                        disabled={selectedCount === 0}
                        style={{
                            textTransform: 'none',
                            color: theme.palette.error.main,
                            borderColor: theme.palette.error.main,
                        }}
                    >
                        Reject {selectedCount} Suggestion
                        {selectedCount !== 1 ? 's' : ''}
                    </Button>
                </div>
            </Container>
        </SideDrawer>
    )
}
